import type { CheckPillarRow, DataDeskRow, DataProjectHealthRow, ProjectCheckRow } from '../../lib/database.types'
import type { DeskFinding } from './findings'

/**
 * A reporting project's standing in each of the four pillars, and the join that
 * narrows its open findings to one of them.
 *
 * Everything here is presentation. The rating is `v_data_desk`'s, which applies
 * the ops contract: a pillar status is the one the project reported where it
 * reports one, and derived from its open findings where it does not. This module
 * never computes, infers or recomputes a status -- not from the findings beside
 * it, not from the open count, not from the overall status. It reads the column
 * the pillar registry names, turns four known strings into a word and a muted
 * token, and leaves everything it does not recognise as `unknown`.
 *
 * That matters more than it sounds. The desk view publishes one pair of columns
 * per pillar that existed when it was written; `check_pillars` is a registry a
 * person adds a row to. A fifth pillar therefore arrives here as a cell with no
 * column behind it, and the honest answer is that nothing is known about it --
 * not that it is passing. Guessing green is how a whole pillar's worth of
 * problems disappears from a desk quietly, which is the same mistake as
 * switching on a `kind` string one view over.
 *
 * Nothing is badged, filled or counted in an alert colour (product rule 1).
 * `red` takes the muted clay of the failure family, `amber` the muted ochre of
 * waiting, and `unknown` plain ink -- never the green of `--s-done`.
 */

export type PillarStatus = 'red' | 'amber' | 'green' | 'unknown'

export interface PillarCell {
  /** The registry's slug, which is both the desk's column prefix and the URL's value. */
  slug: string
  label: string
  /** What the pillar asks, from the registry: "Is it current?". Shown as the cell's title. */
  question: string
  status: PillarStatus
  /** The status in words rather than its colour name. */
  standing: string
  /** Open and not accepted in this pillar, as the view counted them. */
  open: number
}

export interface ProjectPillars {
  ref: string
  /** One per registry row, in `sort_order`. */
  cells: PillarCell[]
  /** Null where 98h-infra expects nothing of the project -- absent, not a failure. */
  contractVersion: string | null
  contractOk: boolean | null
}

const RATED: ReadonlyArray<string> = ['red', 'amber', 'green']

/**
 * The status in words. Deliberately says nothing about *why*: the rating may be
 * the project's own rather than derived, so "critical is open" would be this app
 * inventing a reason for a number it was handed.
 */
const STANDING: Record<PillarStatus, string> = {
  red: 'not holding',
  amber: 'needs a look',
  green: 'holding',
  unknown: 'no check',
}

/** The CSS custom property a status is written in. Muted families only. */
export function pillarTone(status: PillarStatus): string {
  if (status === 'red') return 'var(--s-fail)'
  if (status === 'amber') return 'var(--s-wait)'
  if (status === 'green') return 'var(--s-done)'
  return 'var(--ink-3)'
}

/** Anything that is not one of the three ratings is unknown, including a missing column. */
function statusOf(value: unknown): PillarStatus {
  return typeof value === 'string' && RATED.includes(value) ? (value as PillarStatus) : 'unknown'
}

function openOf(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0
}

/**
 * The desk's rows, laid out one cell per pillar, keyed by project ref.
 *
 * Scoped by the department's own project-health rows rather than by a slug: the
 * view has no department column, and the desk already knows which projects are
 * its department's because it reads their health. A desk with none gets an empty
 * map and renders no cells, which is what makes this sit on any department
 * without naming one (product rule 2).
 *
 * The registry's rows are put back in `sort_order` here as well as in the query
 * that fetches them. The order is the registry's fact, and a mapper that depends
 * on its caller having sorted is one refactor away from rendering Agreement
 * first.
 */
export function pillarsByProject(
  desk: ReadonlyArray<DataDeskRow>,
  pillars: ReadonlyArray<CheckPillarRow>,
  health: ReadonlyArray<DataProjectHealthRow>,
): Map<string, ProjectPillars> {
  const mine = new Set(health.map((h) => h.project_ref))
  const ordered = [...pillars].sort((a, b) => a.sort_order - b.sort_order)
  const out = new Map<string, ProjectPillars>()
  for (const row of desk) {
    const ref = row.project_ref
    if (!ref || !mine.has(ref)) continue
    // The pillar's columns are named by its slug, so they are read by name. A
    // registry row the view has no column for reads as undefined and lands on
    // the unknown fallback below, which is the whole point of doing it this way.
    const columns = row as unknown as Record<string, unknown>
    out.set(ref, {
      ref,
      cells: ordered.map((p) => {
        const status = statusOf(columns[`${p.slug}_status`])
        return {
          slug: p.slug,
          label: p.display_name,
          question: p.question,
          status,
          standing: STANDING[status],
          open: openOf(columns[`${p.slug}_open`]),
        }
      }),
      contractVersion: row.contract_version,
      contractOk: row.contract_ok,
    })
  }
  return out
}

// -- the drill-down --------------------------------------------------------

/** `(project_ref, check_name)` to the pillar that check belongs to. */
export type CheckPillarIndex = ReadonlyMap<string, string>

function keyOf(projectRef: string, checkName: string): string {
  return [projectRef, checkName].join('\n')
}

/**
 * The join the drill-down needs, as a lookup: a finding names a check, and the
 * check says which pillar it is in.
 *
 * A check with no pillar is left out rather than given one. It then matches no
 * selection, so it shows in the project's unfiltered list and in none of the
 * narrowed ones -- visible where everything is, absent where a claim would have
 * to be made about it.
 */
export function checkPillarIndex(rows: ReadonlyArray<ProjectCheckRow>): CheckPillarIndex {
  const index = new Map<string, string>()
  for (const row of rows) {
    if (!row.pillar) continue
    index.set(keyOf(row.project_ref, row.check_name), row.pillar)
  }
  return index
}

/**
 * Whether a finding belongs to the selected pillar.
 *
 * The caller passes findings that are already open and not accepted -- the open
 * board is exactly that -- so this is the pillar half of the filter and nothing
 * else.
 */
export function inPillar(index: CheckPillarIndex, finding: DeskFinding, pillar: string): boolean {
  return index.get(keyOf(finding.projectRef, finding.check)) === pillar
}
